/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import {
  usePropertySettings,
  type PropertyRequiredFields,
  type PropertySettingsTransaction,
} from '@features/property-management/hooks/usePropertySettings';
import { NativeCheckbox, Text } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';

import { BlockMid, SettingsBlock } from './blocks';
import { BlockError, BlockLoading } from './BlockState';
import {
  SettingsItemList, summaryOf, type ItemSectionProps, type ItemSummaries,
} from './SettingsItemList';
import { fieldLabelStyle } from './settings-block-styles';
import { useRegisterSettingsDirty } from '../hooks/use-settings-dirty';
import { itemsOf } from '../constants/settings-items';

const TRANSACTIONS: PropertySettingsTransaction[] = ['sale', 'rent'];

const EMPTY_REQUIRED: PropertyRequiredFields = { sale: [], rent: [] };

/**
 * The price that belongs to the other transaction: asking for the rent on a
 * sale-only listing would block every sale, so each list leaves it out.
 */
const FOREIGN_PRICE: Record<PropertySettingsTransaction, string> = {
  sale: 'rental_price',
  rent: 'sale_price',
};

/**
 * Whether two required-field maps hold the same fields, ignoring order.
 *
 * @param a - One map.
 * @param b - The other.
 * @returns True when every transaction lists the same fields.
 */
function sameRequired(a: PropertyRequiredFields, b: PropertyRequiredFields): boolean {
  return TRANSACTIONS.every((tx) =>
    a[tx].length === b[tx].length && a[tx].every((field) => b[tx].includes(field)));
}

/**
 * Returns *fields* with *field* switched on or off.
 *
 * @param fields - The current list.
 * @param field - The field being toggled.
 * @param on - Whether it should now be required.
 * @returns The new list.
 */
function toggled(fields: string[], field: string, on: boolean): string[] {
  const without = fields.filter((f) => f !== field);
  return on ? [...without, field] : without;
}

/**
 * Configurações > Imóveis. One setting today, behind the same list the other
 * tabs open on, so the next one is a row rather than another block below.
 *
 * @returns The property settings section.
 */
export function PropertySettingsSection({ item = null, onOpenItem = () => undefined }: ItemSectionProps) {
  const summaries = usePropertySummaries();
  if (item === 'propertyRequired') return <RequiredFieldsBlock />;
  return <SettingsItemList items={itemsOf('imoveis')} summaries={summaries} onOpen={onOpenItem} />;
}

/**
 * Each property setting's current value, in the words the list shows.
 *
 * @returns The summaries keyed by item.
 */
function usePropertySummaries(): ItemSummaries {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const property = usePropertySettings(agency.id ?? '');
  const required = property.settings?.required_fields ?? EMPTY_REQUIRED;

  return {
    propertyRequired: summaryOf(property, () => t('v2.items.propertyRequired.summary', {
      sale: required.sale.length, rent: required.rent.length,
    })),
  };
}

/**
 * Which fields a broker must fill before saving a property, one checklist
 * per transaction side by side. The floor the form always requires sits on
 * top as labels: it is not a choice, so it gets no controls. The rule is
 * applied by the form alone, so imports keep accepting incomplete rows.
 *
 * @returns The required-fields block.
 */
function RequiredFieldsBlock() {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((state) => state);
  const { canManagePropertySettings: canManage } = useAgencyPermissions();
  const {
    settings, isLoading, isError, refetch, saveRequiredFields,
  } = usePropertySettings(agency.id ?? '');
  const stored = settings?.required_fields ?? EMPTY_REQUIRED;
  const [draft, setDraft] = useState<PropertyRequiredFields>(stored);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(settings?.required_fields ?? EMPTY_REQUIRED);
  }, [settings]);

  const isDirty = useMemo(() => !sameRequired(draft, stored), [draft, stored]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveRequiredFields(draft);
      notifications.show({ color: 'green', message: t('v2.imoveis.required.saved') });
    } catch {
      notifications.show({ color: 'red', message: t('v2.imoveis.required.saveError') });
    } finally {
      setSaving(false);
    }
  };

  useRegisterSettingsDirty({
    isDirty: canManage && isDirty,
    isSaving: saving,
    onSave: () => void handleSave(),
    onDiscard: () => setDraft(stored),
  });

  const fieldLabel = (field: string) => t(`v2.imoveis.fields.${field}`, { defaultValue: field });

  const renderBody = () => {
    if (isLoading) return <BlockLoading rows={6} />;
    if (isError || !settings) return <BlockError onRetry={() => void refetch()} />;
    return (
      <>
        <BlockMid>
          <div className="flex flex-wrap items-center gap-(--space-2)" data-testid="property-floor">
            <Text component="span" style={{ ...fieldLabelStyle, marginRight: 'var(--space-1)' }}>
              {t('v2.imoveis.floor.title')}
            </Text>
            {settings.locked_fields.map((field) => (
              <Text
                key={field}
                component="span"
                style={{
                  padding: '2px var(--space-2-5)', borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-subtle)', background: 'var(--surface-primary)',
                  fontSize: 'var(--text-meta)', fontWeight: 600, color: 'var(--text-primary)',
                }}
              >
                {fieldLabel(field)}
              </Text>
            ))}
          </div>
        </BlockMid>
        <div
          className="grid items-start gap-(--space-4)"
          style={{ padding: '0 var(--space-5) var(--space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}
        >
          {TRANSACTIONS.map((tx) => (
            <TransactionChecklist
              key={tx}
              transaction={tx}
              fields={settings.configurable_fields.filter((field) => field !== FOREIGN_PRICE[tx])}
              checked={draft[tx]}
              disabled={!canManage || saving}
              labelOf={fieldLabel}
              onToggle={(field, on) => setDraft((prev) => ({ ...prev, [tx]: toggled(prev[tx], field, on) }))}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <SettingsBlock
      title={t('v2.items.propertyRequired.title')}
      caption={t('v2.imoveis.required.explanation')}
      note={t('v2.imoveis.required.note')}
      testId="property-required-block"
    >
      {renderBody()}
    </SettingsBlock>
  );
}

interface TransactionChecklistProps {
  transaction: PropertySettingsTransaction;
  fields: string[];
  checked: string[];
  disabled: boolean;
  labelOf: (field: string) => string;
  onToggle: (field: string, on: boolean) => void;
}

/**
 * One transaction's fields as a two-column checklist under a header that
 * counts what is required.
 *
 * @returns The checklist.
 */
function TransactionChecklist({
  transaction, fields, checked, disabled, labelOf, onToggle,
}: TransactionChecklistProps) {
  const { t } = useTranslation('features/agency-settings');
  const title = t(`v2.imoveis.required.blocks.${transaction}.title`);

  return (
    <fieldset
      data-testid={`property-required-${transaction}`}
      style={{
        margin: 0, padding: 0, minWidth: 0, overflow: 'hidden',
        border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)',
        background: 'var(--surface-primary)',
      }}
    >
      <Text
        component="legend"
        className="flex w-full items-center justify-between gap-(--space-2)"
        style={{
          float: 'left', boxSizing: 'border-box', padding: 'var(--space-2-5) var(--space-3-5)',
          background: 'var(--surface-secondary)', borderBottom: '1px solid var(--border-ultra-subtle)',
        }}
      >
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 700, color: 'var(--text-primary)' }}>
          {title}
        </Text>
        <Text component="span" style={{ fontSize: 'var(--text-meta)', fontWeight: 600, color: 'var(--text-tertiary-soft)' }}>
          {t('v2.imoveis.required.count', { count: checked.length })}
        </Text>
      </Text>
      <div className="grid gap-x-(--space-2)" style={{ clear: 'both', padding: 'var(--space-2)', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {fields.map((field) => {
          const id = `property-required-${transaction}-${field}`;
          return (
            <Text
              as="label"
              key={field}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-(--space-2-5) rounded-(--radius-md) hover:bg-(--surface-hover)"
              style={{ minHeight: 40, padding: '0 var(--space-2-5)', fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}
            >
              <NativeCheckbox
                id={id}
                checked={checked.includes(field)}
                disabled={disabled}
                onChange={(e) => onToggle(field, e.target.checked)}
                data-testid={id}
              />
              {labelOf(field)}
            </Text>
          );
        })}
      </div>
    </fieldset>
  );
}
